import { ChangeDetectionStrategy, Component } from '@angular/core';
import { SaleDocketStore } from '../../sale-docket.store';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import {
  filter,
  map,
  startWith,
  take,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';
import { ConfigService } from 'src/app/shared/services/config.service';
import { BehaviorSubject } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';

@Component({
    selector: 'app-edit-sales-docket-settings',
    templateUrl: './edit-sales-docket-settings.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [MatDialogTitle, MatDialogContent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatRadioGroup, MatRadioButton, NgIf, MatSelect, NgFor, MatOption, MatDialogActions, MatButton, MatDialogClose, AsyncPipe]
})
export class EditSalesDocketSettingsComponent {
  form = new FormGroup({
    purchase_order: new FormControl<string>(''),
    account: new FormControl<boolean>(false),
    currency_code: new FormControl<string>(''),
    currency_rate: new FormControl<number>(1)
  });

  hideCurrencyRateField$ = new BehaviorSubject(true);

  constructor(
    private store: SaleDocketStore,
    private dialog: MatDialogRef<EditSalesDocketSettingsComponent>,
    private configService: ConfigService
  ) {
    this.store.docket$
      .pipe(take(1))
      .subscribe(
        ({ purchase_order, account, currency_code, currency_rate }) => {
          this.form.patchValue({
            purchase_order,
            account,
            currency_code,
            currency_rate
          });
        }
      );

    this.form.controls['currency_code'].valueChanges
      .pipe(
        startWith(this.form.controls['currency_code'].value),
        withLatestFrom(this.defaultCurrency$),
        takeUntil(this.dialog.afterClosed())
      )
      .subscribe(([current, defaultCurrency]) => {
        const currencyRateField = this.form.controls['currency_rate'];
        this.hideCurrencyRateField$.next(current === defaultCurrency);
        if (current === defaultCurrency) {
          currencyRateField.setValue(1);
          currencyRateField.setValidators(null);
        } else {
          currencyRateField.enable();
          currencyRateField.setValidators(Validators.required);
          if (currencyRateField.value === 1) {
            currencyRateField.patchValue(null);
          }
        }
      });
  }

  get defaultCurrency$() {
    return this.configService.userConfig.pipe(
      startWith({ currency: 'GBP' }),
      filter((config) => 'currency' in config),
      map((config) => config['currency'])
    );
  }

  get currencies$() {
    return this.configService.userConfig.pipe(
      startWith({}),
      filter((config) => 'additional_currencies' in config),
      map((config) =>
        [config['currency'] ?? 'GBP'].concat(
          config['additional_currencies']
            .split(',')
            .map((value) => value.trim())
        )
      )
    );
  }

  save() {
    this.store.updateDocket({
      value: this.form.value,
      onComplete: () => this.dialog.close()
    });
  }
}
